import axios from "axios";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import type { ActiveTeamAccessResult, Team } from "./teams.types";

export const TEAM_ACCESS_NOT_FOUND_TITLE = {
  teamDeleted: "Team Not Found",
  userDeassigned: "User Not In Team",
  noTeamMembership: "No team membership",
  roleAssignmentNotFound: "Role Assignment Not Found",
} as const;

export const TEAM_ACCESS_NOT_FOUND_TYPE = {
  roleAssignmentNotFound: "RoleAssignmentNotFound",
} as const;

export const TEAM_ACCESS_LOST_PROBLEM_TYPE = {
  userNotMemberOfAnyTeam: "UserNotMemberOfAnyTeam",
} as const;

type AccessLostProblem = Pick<ProblemDetails, "title" | "type">;
type AccessLostKind = Exclude<ActiveTeamAccessResult["kind"], "ok">;

const USER_DEASSIGNED_TITLES: ReadonlySet<string> = new Set([
  TEAM_ACCESS_NOT_FOUND_TITLE.userDeassigned,
  TEAM_ACCESS_NOT_FOUND_TITLE.noTeamMembership,
]);

const ACCESS_LOST_HTTP_STATUSES = new Set([404, 403]);

const ACCESS_LOST_COPY_KEYS = {
  userDeassigned: "settings:teams.accessLost.userDeassigned",
  teamDeleted: "settings:teams.accessLost.teamDeleted",
} as const;

export const mapAccessLostProblemToKind = (
  problem?: AccessLostProblem
): AccessLostKind | undefined => {
  if (!problem) {
    return undefined;
  }

  if (problem.title === TEAM_ACCESS_NOT_FOUND_TITLE.teamDeleted) {
    return "teamDeleted";
  }

  if (
    (problem.title !== undefined &&
      USER_DEASSIGNED_TITLES.has(problem.title)) ||
    problem.type === TEAM_ACCESS_LOST_PROBLEM_TYPE.userNotMemberOfAnyTeam
  ) {
    return "userDeassigned";
  }

  return undefined;
};

export const getAccessLostProblemFromError = (
  error: unknown
): AccessLostProblem | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const status = error.response?.status;
  if (status === undefined || !ACCESS_LOST_HTTP_STATUSES.has(status)) {
    return undefined;
  }

  return error.response?.data as AccessLostProblem | undefined;
};

export const resolveAccessLostKindFromError = (
  error: unknown
): AccessLostKind | undefined =>
  mapAccessLostProblemToKind(getAccessLostProblemFromError(error));

export const resolveActiveTeamAccessResult = (
  data?: ActiveTeamAccessResult,
  error?: unknown
): ActiveTeamAccessResult | undefined => {
  if (data && data.kind !== "ok") {
    return data;
  }

  const accessLostKind = resolveAccessLostKindFromError(error);
  if (accessLostKind) {
    return { kind: accessLostKind };
  }

  return data;
};

export const getActiveTeamAccessFromTeamsList = (
  activeTeamId: string,
  teams: Team[] | undefined,
  isTeamsLoaded: boolean,
  apiFailed = false
): ActiveTeamAccessResult | undefined => {
  if (!activeTeamId || !isTeamsLoaded || !teams || apiFailed) {
    return undefined;
  }

  if (teams.some((team) => team.id === activeTeamId)) {
    return undefined;
  }

  return { kind: "userDeassigned" };
};

export const isActiveTeamAccessLost = (
  result?: ActiveTeamAccessResult
): boolean =>
  result?.kind === "teamDeleted" || result?.kind === "userDeassigned";

export const getActiveTeamAccessLostCopyKey = (
  kind?: ActiveTeamAccessResult["kind"]
): string =>
  kind === "userDeassigned"
    ? ACCESS_LOST_COPY_KEYS.userDeassigned
    : ACCESS_LOST_COPY_KEYS.teamDeleted;

const TEAM_ACCESS_NOT_FOUND_TYPES = new Set<string>([
  TEAM_ACCESS_NOT_FOUND_TYPE.roleAssignmentNotFound,
]);

const TEAM_ACCESS_NOT_FOUND_TITLES = new Set<string>([
  TEAM_ACCESS_NOT_FOUND_TITLE.teamDeleted,
  TEAM_ACCESS_NOT_FOUND_TITLE.userDeassigned,
  TEAM_ACCESS_NOT_FOUND_TITLE.noTeamMembership,
  TEAM_ACCESS_NOT_FOUND_TITLE.roleAssignmentNotFound,
]);

const getProblemTypeAndTitle = (
  error?: ProblemDetails
): { type?: string; title?: string } => ({
  type: error?.type ?? error?.details?.type,
  title: error?.title ?? error?.details?.title,
});

export const isTeamAccessNotFoundProblem = (
  error?: ProblemDetails
): boolean => {
  const { type, title } = getProblemTypeAndTitle(error);

  return (
    (type !== undefined && TEAM_ACCESS_NOT_FOUND_TYPES.has(type)) ||
    (title !== undefined && TEAM_ACCESS_NOT_FOUND_TITLES.has(title))
  );
};
